import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, CheckCircle2, XCircle, ArrowRight, RotateCcw, Trophy, Loader2, Lock,
} from 'lucide-react';
import { interactionsApi } from '../../services/api';
import { isTwa } from '../../utils/platform';
import { QuizQuestion } from '../../types';

interface MemoQuizProps {
  jobId: string;
}

export function MemoQuiz({ jobId }: MemoQuizProps) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [error, setError] = useState('');

  const startQuiz = async (regenerate = false) => {
    setError('');
    setIsGenerating(true);
    try {
      if (regenerate) await interactionsApi.clearMessages(jobId, 'quiz');
      const { data } = await interactionsApi.generateQuiz(jobId);
      setQuestions(data.questions);
      setCurrent(0);
      setSelected(null);
      setAnswers([]);
      setIsFinished(false);
    } catch (err: any) {
      if (err.response?.status === 403) setQuotaExhausted(true);
      else setError(err.response?.data?.detail || 'Failed to generate quiz. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const retake = () => {
    setCurrent(0);
    setSelected(null);
    setAnswers([]);
    setIsFinished(false);
  };

  const choose = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    setAnswers((prev) => [...prev, idx === questions[current].correct_index]);
  };

  const next = () => {
    if (current + 1 >= questions.length) {
      setIsFinished(true);
    } else {
      setCurrent((c) => c + 1);
      setSelected(null);
    }
  };

  // --- Start screen ---
  if (questions.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-legal-200 p-10 flex flex-col items-center text-center">
        <div className="h-12 w-12 rounded-full bg-accent-50 flex items-center justify-center mb-4">
          <GraduationCap className="h-6 w-6 text-accent-600" />
        </div>
        <h3 className="font-semibold text-legal-900 mb-1">Test your command of this memo</h3>
        <p className="text-sm text-legal-500 mb-6 max-w-md">
          A quick quiz built from your research — case holdings, citations and reasoning.
          Perfect before a hearing, a viva, or briefing a senior.
        </p>
        {quotaExhausted ? (
          <div className="flex items-center space-x-3">
            <span className="flex items-center text-sm text-legal-600">
              <Lock className="h-4 w-4 mr-1.5 text-accent-600" />
              Free interactions used up for this memo.
            </span>
            {isTwa() ? (
              <span className="text-sm text-legal-600">Upgrade at urava.app for unlimited.</span>
            ) : (
              <Link
                to="/pricing"
                data-track="quiz-quota-upgrade"
                className="text-sm font-medium px-4 py-2 bg-legal-900 text-white rounded-lg hover:bg-legal-800 transition-colors"
              >
                Upgrade
              </Link>
            )}
          </div>
        ) : (
          <button
            onClick={() => startQuiz()}
            disabled={isGenerating}
            className="flex items-center space-x-2 px-5 py-2.5 bg-legal-900 text-white rounded-lg text-sm font-medium hover:bg-legal-800 transition-colors disabled:opacity-60"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Preparing questions from your memo…</span>
              </>
            ) : (
              <span>Test me</span>
            )}
          </button>
        )}
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  // --- Results screen ---
  if (isFinished) {
    const score = answers.filter(Boolean).length;
    const pct = Math.round((score / questions.length) * 100);
    const verdict =
      pct === 100 ? 'Flawless. Ready for the bench.' :
      pct >= 75 ? 'Strong grasp — polish the misses and you’re set.' :
      pct >= 50 ? 'Decent foundation. Revisit the memo sections you missed.' :
      'Worth a careful re-read of the memo before relying on it.';
    return (
      <div className="bg-white rounded-lg border border-legal-200 p-10 flex flex-col items-center text-center">
        <div className="h-14 w-14 rounded-full bg-accent-50 flex items-center justify-center mb-4">
          <Trophy className="h-7 w-7 text-accent-600" />
        </div>
        <p className="text-4xl font-bold text-legal-900 mb-1">
          {score}<span className="text-legal-400 text-2xl">/{questions.length}</span>
        </p>
        <p className="text-sm text-legal-600 mb-6">{verdict}</p>
        <div className="flex items-center space-x-3">
          <button
            onClick={retake}
            className="flex items-center space-x-1.5 px-4 py-2 border border-legal-300 rounded-lg text-sm font-medium text-legal-700 hover:bg-legal-50 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Retake</span>
          </button>
          <button
            onClick={() => startQuiz(true)}
            disabled={isGenerating}
            className="flex items-center space-x-1.5 px-4 py-2 bg-legal-900 text-white rounded-lg text-sm font-medium hover:bg-legal-800 transition-colors disabled:opacity-60"
          >
            {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            <span>New Questions</span>
          </button>
        </div>
      </div>
    );
  }

  // --- Question screen ---
  const q = questions[current];
  return (
    <div className="bg-white rounded-lg border border-legal-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium text-legal-400">
          Question {current + 1} of {questions.length}
        </span>
        {q.memo_section && (
          <span className="text-xs bg-legal-100 text-legal-600 px-2 py-1 rounded-full">
            {q.memo_section}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-legal-100 rounded-full mb-6">
        <div
          className="h-1.5 bg-accent rounded-full transition-all"
          style={{ width: `${((current + (selected !== null ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      <h3 className="text-lg font-semibold text-legal-900 mb-5">{q.question}</h3>

      <div className="space-y-2.5">
        {q.options.map((opt, idx) => {
          const isCorrect = idx === q.correct_index;
          const isChosen = idx === selected;
          let cls = 'border-legal-200 hover:border-legal-400 text-legal-700';
          if (selected !== null) {
            if (isCorrect) cls = 'border-green-500 bg-green-50 text-green-900';
            else if (isChosen) cls = 'border-red-400 bg-red-50 text-red-900';
            else cls = 'border-legal-200 text-legal-400';
          }
          return (
            <button
              key={idx}
              onClick={() => choose(idx)}
              disabled={selected !== null}
              className={`w-full flex items-center justify-between text-left px-4 py-3 border rounded-lg text-sm transition-colors ${cls}`}
            >
              <span>{opt}</span>
              {selected !== null && isCorrect && <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 ml-2" />}
              {selected !== null && isChosen && !isCorrect && <XCircle className="h-5 w-5 text-red-500 shrink-0 ml-2" />}
            </button>
          );
        })}
      </div>

      {selected !== null && (
        <div className="mt-5">
          <div className="p-4 bg-legal-50 border border-legal-200 rounded-lg">
            <p className="text-sm text-legal-700">{q.explanation}</p>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={next}
              className="flex items-center space-x-1.5 px-5 py-2.5 bg-legal-900 text-white rounded-lg text-sm font-medium hover:bg-legal-800 transition-colors"
            >
              <span>{current + 1 >= questions.length ? 'See Results' : 'Next'}</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
